import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Checkbox } from 'src/components/ui/checkbox';
import { Search, Loader2 } from 'lucide-react';
import { TestService } from 'src/services/api/TestService';
import { useToast } from 'src/hooks/use-toast';

export const TestGroupSelectionModal = ({
  open,
  onClose,
  onAdd,
  selectedGroupIds = [],
}) => {
  const { toast } = useToast();
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const observerTarget = useRef(null);

  const pageSize = 20;

  const fetchGroups = async (currentPage, searchQuery, reset = false) => {
    try {
      if (currentPage === 1) {
        setInitialLoading(true);
      } else {
        setLoading(true);
      }

      const res = await TestService.listAllTestGroups(
        currentPage,
        pageSize,
        searchQuery,
        'ACTIVE'
      );

      const items = res?.data?.items || [];
      const total = res?.data?.pagination?.total || 0;

      if (reset) {
        setGroups(items);
      } else {
        setGroups((prev) => [...prev, ...items]);
      }

      setHasMore(items.length > 0 && groups.length + items.length < total);
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast({
        message: 'Failed to fetch test groups',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setGroups([]);
      setPage(1);
      setSelectedGroups([]);
      setSearch('');
      fetchGroups(1, '', true);
    }
  }, [open]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setGroups([]);
      setPage(1);
      fetchGroups(1, search, true);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !loading &&
          !initialLoading
        ) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchGroups(nextPage, search, false);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loading, initialLoading, page, search]);

  const handleToggleGroup = (group) => {
    setSelectedGroups((prev) => {
      const exists = prev.find((g) => g.id === group.id);
      if (exists) {
        return prev.filter((g) => g.id !== group.id);
      }
      return [...prev, group];
    });
  };

  const handleAdd = () => {
    onAdd(selectedGroups);
    onClose();
  };

  const isGroupSelected = (groupId) =>
    selectedGroups.some((g) => g.id === groupId);

  const isGroupAlreadyAdded = (groupId) => selectedGroupIds.includes(groupId);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Select Test Groups</DialogTitle>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search groups..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex-1 overflow-y-auto border border-border rounded-lg max-h-[40vh]">
            {initialLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}
            {!initialLoading && groups.length === 0 && (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                No groups found
              </div>
            )}
            {!initialLoading && groups.length > 0 && (
              <div className="divide-y divide-border">
                {groups.map((group) => {
                  const isSelected = isGroupSelected(group.id);
                  const isAlreadyAdded = isGroupAlreadyAdded(group.id);
                  const disabledClass = isAlreadyAdded
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer';

                  return (
                    <div
                      key={group.id}
                      role="button"
                      tabIndex={isAlreadyAdded ? -1 : 0}
                      className={`p-4 hover:bg-muted/50 transition-colors ${disabledClass}`}
                      onClick={() =>
                        !isAlreadyAdded && handleToggleGroup(group)
                      }
                      onKeyDown={(e) => {
                        if (
                          (e.key === 'Enter' || e.key === ' ') &&
                          !isAlreadyAdded
                        ) {
                          e.preventDefault();
                          handleToggleGroup(group);
                        }
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={isSelected}
                          disabled={isAlreadyAdded}
                          onCheckedChange={() =>
                            !isAlreadyAdded && handleToggleGroup(group)
                          }
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-foreground-strong">
                              {group.name}
                              {isAlreadyAdded && (
                                <span className="ml-2 text-xs text-muted-foreground font-normal">
                                  (Already added)
                                </span>
                              )}
                            </h4>
                            <span className="text-xs text-muted-foreground">
                              ID: {group.group_id || group.id}
                            </span>
                          </div>
                          {group.description && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {group.description}
                            </p>
                          )}
                          {group.test_ref && (
                            <p className="text-xs text-primary mt-1">
                              Test Ref: {group.test_ref}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {hasMore && (
                  <div ref={observerTarget} className="p-4 flex justify-center">
                    {loading && (
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={selectedGroups.length === 0}
            className="min-w-[120px]"
          >
            Add {selectedGroups.length > 0 && `(${selectedGroups.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
